import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../services/axiosInstance';
import { toggleLikeReview, toggleDislikeReview, deleteReview } from './review.slice';

const initialState = {
  blogs: [],
  stats: {
    totalBlogs: 0,
    published: 0,
    drafts: 0,
    totalViews: 0,
    popularCategories: [],
    recentReviews: [],
    chartData: { labels: ['May 1', 'May 8', 'May 15', 'May 22', 'May 29'], values: [0, 0, 0, 0, 0] }
  },
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 6
  },
  currentBlog: null,
  loading: false,
  currentLoadingPayload: null,
  actionLoading: false,
  error: null,
};

// Fetch Blogs
export const fetchBlogsThunk = createAsyncThunk(
  'blog/fetchBlogs',
  async (params = {}, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/blogs`, { params });
      if (res.data.success) {
        return res.data;
      }
      return rejectWithValue('Failed to fetch blogs');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  },
  {
    condition: (params = {}, { getState }) => {
      const { blog } = getState();
      
      // If we are currently loading this exact same request, skip
      if (blog.loading && JSON.stringify(blog.currentLoadingPayload) === JSON.stringify(params)) {
        return false;
      }

      // Only cache if no params, simple fetch, or header fetch (limit: 20)
      const hasNoParams = !params || Object.keys(params).length === 0;
      const isHeaderFetch = params && params.limit === 20 && Object.keys(params).length === 1;

      if (hasNoParams || isHeaderFetch) {
        if (blog.blogs && blog.blogs.length > 0) {
          return false;
        }
      }
    }
  }
);

// Create Blog
export const createBlogThunk = createAsyncThunk(
  'blog/createBlog',
  async (formData, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post('/blogs', formData);
      if (res.data.success) {
        return res.data.blog;
      }
      return rejectWithValue('Failed to create blog');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Update Blog
export const updateBlogThunk = createAsyncThunk(
  'blog/updateBlog',
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.put(`/blogs/${id}`, formData);
      if (res.data.success) {
        return res.data.blog;
      }
      return rejectWithValue('Failed to update blog');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Delete Blog
export const deleteBlogThunk = createAsyncThunk(
  'blog/deleteBlog',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.delete(`/blogs/${id}`);
      if (res.data.success) {
        return id;
      }
      return rejectWithValue('Failed to delete blog');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Fetch Single Blog by ID
export const fetchBlogByIdThunk = createAsyncThunk(
  'blog/fetchBlogById',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get(`/blogs/${id}`);
      if (res.data.success) {
        return res.data.blog;
      }
      return rejectWithValue('Failed to fetch blog');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Like/Unlike Blog
export const likeBlogThunk = createAsyncThunk(
  'blog/likeBlog',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`/blogs/${id}/like`);
      if (res.data.success) {
        return { id, likes: res.data.likes };
      }
      return rejectWithValue('Action failed');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Add Comment to Blog
export const addCommentThunk = createAsyncThunk(
  'blog/addComment',
  async ({ id, comment, rating }, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.post(`/blogs/${id}/comments`, { comment, rating });
      if (res.data.success) {
        return { id, comment: res.data.comment };
      }
      return rejectWithValue('Failed to post comment');
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

// Fetch all blog comments for admin
export const fetchAdminBlogCommentsThunk = createAsyncThunk(
  'blog/fetchAdminComments',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/blogs/admin/comments');
      return response.data.comments;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Error fetching blog comments');
    }
  }
);

export const blogSlice = createSlice({
  name: 'blog',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch Blogs
      .addCase(fetchBlogsThunk.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.currentLoadingPayload = action.meta.arg;
      })
      .addCase(fetchBlogsThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentLoadingPayload = null;
        if (action.payload.pagination && action.payload.pagination.currentPage > 1 && !action.meta.arg?.isAdmin) {
          state.blogs = [...state.blogs, ...action.payload.blogs];
        } else {
          state.blogs = action.payload.blogs;
        }
        if (action.payload.stats) state.stats = action.payload.stats;
        if (action.payload.pagination) state.pagination = action.payload.pagination;
      })
      .addCase(fetchBlogsThunk.rejected, (state, action) => {
        state.loading = false;
        state.currentLoadingPayload = null;
        state.error = action.payload;
      })

      // Create Blog
      .addCase(createBlogThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(createBlogThunk.fulfilled, (state, action) => {
        state.actionLoading = false;
        state.blogs.unshift(action.payload);
        state.stats.totalBlogs += 1;
        if (action.payload.status === 'Published') {
          state.stats.published += 1;
        } else {
          state.stats.drafts += 1;
        }
      })
      .addCase(createBlogThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })

      // Update Blog
      .addCase(updateBlogThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(updateBlogThunk.fulfilled, (state, action) => {
        state.actionLoading = false;
        const index = state.blogs.findIndex(b => b._id === action.payload._id);
        if (index !== -1) {
          const oldBlog = state.blogs[index];
          state.blogs[index] = action.payload;

          // Adjust stats if status changed
          if (oldBlog.status !== action.payload.status) {
            if (action.payload.status === 'Published') {
              state.stats.published += 1;
              state.stats.drafts = Math.max(0, state.stats.drafts - 1);
            } else {
              state.stats.drafts += 1;
              state.stats.published = Math.max(0, state.stats.published - 1);
            }
          }
        }
      })
      .addCase(updateBlogThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })

      // Delete Blog
      .addCase(deleteBlogThunk.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deleteBlogThunk.fulfilled, (state, action) => {
        state.actionLoading = false;
        const deletedId = action.payload;
        const index = state.blogs.findIndex(b => b._id === deletedId);
        if (index !== -1) {
          const deletedBlog = state.blogs[index];
          state.blogs.splice(index, 1);
          state.stats.totalBlogs = Math.max(0, state.stats.totalBlogs - 1);
          if (deletedBlog.status === 'Published') {
            state.stats.published = Math.max(0, state.stats.published - 1);
          } else {
            state.stats.drafts = Math.max(0, state.stats.drafts - 1);
          }
        }
      })
      .addCase(deleteBlogThunk.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })

      // Fetch Single Blog
      .addCase(fetchBlogByIdThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBlogByIdThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.currentBlog = action.payload;
      })
      .addCase(fetchBlogByIdThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Like Blog
      .addCase(likeBlogThunk.fulfilled, (state, action) => {
        if (state.currentBlog && state.currentBlog._id === action.payload.id) {
          state.currentBlog.likes = action.payload.likes;
        }
        // Also update in list
        const index = state.blogs.findIndex(b => b._id === action.payload.id);
        if (index !== -1) {
          state.blogs[index].likes = action.payload.likes;
        }
      })

      // Add Comment
      .addCase(addCommentThunk.fulfilled, (state, action) => {
        if (state.currentBlog && state.currentBlog._id === action.payload.id) {
          if (!state.currentBlog.comments) state.currentBlog.comments = [];
          state.currentBlog.comments.unshift(action.payload.comment);
          if (!state.currentBlog.reviews) state.currentBlog.reviews = [];
          state.currentBlog.reviews.unshift(action.payload.comment);
        }
      })
      // Synchronize review slice actions into blog detail reviews list locally (Single API Call Pattern)
      .addCase(toggleLikeReview.fulfilled, (state, action) => {
        if (state.currentBlog && state.currentBlog.reviews) {
          const { reviewId, likes, dislikes } = action.payload;
          const review = state.currentBlog.reviews.find(r => (r._id === reviewId || r.id === reviewId));
          if (review) {
            review.likes = likes;
            review.dislikes = dislikes;
          }
        }
      })
      .addCase(toggleDislikeReview.fulfilled, (state, action) => {
        if (state.currentBlog && state.currentBlog.reviews) {
          const { reviewId, likes, dislikes } = action.payload;
          const review = state.currentBlog.reviews.find(r => (r._id === reviewId || r.id === reviewId));
          if (review) {
            review.likes = likes;
            review.dislikes = dislikes;
          }
        }
      })
      .addCase(deleteReview.fulfilled, (state, action) => {
        if (state.currentBlog && state.currentBlog.reviews) {
          state.currentBlog.reviews = state.currentBlog.reviews.filter(
            r => r._id !== action.payload && r.id !== action.payload
          );
        }
      });
  }
});

export default blogSlice.reducer;
